/**
 * DocumentsSection — Project documents tab (R1/R3: Document Management spec)
 *
 * Shows documents grouped by semantic category:
 *   - Permit Documents  (folders whose name contains "permit")
 *   - Internal Milestones (folders whose name contains "milestone")
 *   - General (all remaining folders + unfiled)
 *
 * When projectId is provided (project page), shows a "Project documents |
 * Tenant library" toggle top-right. Default = "Project documents" (project-
 * scoped, V148 project_id filter). Tenant library shows NULL-project_id docs.
 *
 * When projectId is absent (standalone/settings page), defaults to tenant
 * library view.
 *
 * Backend: V074 (tables), V076 (deliverable_documents), V148 (project_id).
 */
import { useState, useRef } from 'react';
import {
  Upload,
  Download,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileText,
  Link as LinkIcon,
  Plus,
  History,
  FolderOpen,
  Folder,
  Building2,
  Library,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import {
  useDocumentFolders,
  useDocuments,
  useUploadDocument,
  useDeleteDocument,
  useDocument,
  useUploadVersion,
  type Document,
  type DocumentFolder,
  type DocumentVersion,
} from '@/hooks/useDocuments';
import { useDeliverables } from '@/hooks/useDeliverables';
import { useFeature } from '@/lib/features/FeatureProvider';
import { DocumentRequirementsChecklist } from '@/components/projects/documents/DocumentRequirementsChecklist';

// ============================================================================
// Category definitions
// ============================================================================

type DocumentCategory = 'permit' | 'milestone' | 'general';

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  permit: 'Permit Documents',
  milestone: 'Internal Milestones',
  general: 'General',
};

/** Classify a folder (or null = unfiled) into one of the three categories.
 * Exported for unit testing.
 */
export function classifyFolder(folderName: string | null): DocumentCategory {
  if (!folderName) return 'general';
  const lower = folderName.toLowerCase();
  if (lower.includes('permit')) return 'permit';
  if (lower.includes('milestone')) return 'milestone';
  return 'general';
}

// ============================================================================
// Helpers
// ============================================================================

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ============================================================================
// Version History Panel (inline expand)
// ============================================================================

interface VersionHistoryProps {
  documentId: string;
}

function VersionHistory({ documentId }: VersionHistoryProps) {
  const { data, isLoading } = useDocument(documentId);
  const uploadVersion = useUploadVersion();
  const fileRef = useRef<HTMLInputElement>(null);
  const [changeNote, setChangeNote] = useState('');

  const handleNewVersion = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadVersion.mutateAsync({ documentId, file, changeNote: changeNote || undefined });
    setChangeNote('');
    if (fileRef.current) fileRef.current.value = '';
  };

  if (isLoading) {
    return <div className="text-xs text-muted-foreground py-2 px-3">Loading versions…</div>;
  }

  const versions: DocumentVersion[] = data?.versions ?? [];

  return (
    <div className="bg-muted/30 border-t px-3 py-2 space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Version History</p>
      {versions.length === 0 ? (
        <p className="text-xs text-muted-foreground">No versions recorded.</p>
      ) : (
        <div className="space-y-1">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center gap-2 text-xs">
              <span className="font-mono bg-primary/10 text-primary rounded px-1">
                v{v.version_number}
              </span>
              <span className="text-muted-foreground truncate flex-1">
                {v.file_name}
              </span>
              <span className="text-muted-foreground shrink-0">
                {formatFileSize(v.file_size)}
              </span>
              {v.uploaded_by && (
                <span className="text-muted-foreground truncate max-w-[96px] shrink-0">
                  {v.uploaded_by}
                </span>
              )}
              <span className="text-muted-foreground shrink-0">
                {formatDate(v.created_at)}
              </span>
              <button
                className="p-1 hover:bg-muted rounded shrink-0"
                title="Download this version"
                onClick={() => api.documents.downloadVersion(v.id, v.file_name)}
              >
                <Download className="w-3 h-3 text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Upload new version */}
      <div className="flex items-center gap-2 pt-1">
        <Input
          placeholder="Change note (optional)"
          value={changeNote}
          onChange={(e) => setChangeNote(e.target.value)}
          className="h-7 text-xs flex-1"
        />
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => fileRef.current?.click()}
          disabled={uploadVersion.isPending}
        >
          <Upload className="w-3 h-3 mr-1" />
          New Version
        </Button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={handleNewVersion}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Document Row
// ============================================================================

interface DocumentRowProps {
  doc: Document;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

function DocumentRow({ doc, isExpanded, onToggle, onDelete }: DocumentRowProps) {
  return (
    <>
      <tr
        className="border-t hover:bg-muted/30 group cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-3 py-2">
          {isExpanded
            ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
            : <ChevronRight className="w-4 h-4 text-muted-foreground" />
          }
        </td>
        <td className="px-3 py-2">
          {doc.document_type === 'link'
            ? <LinkIcon className="w-4 h-4 text-blue-500" />
            : <FileText className="w-4 h-4 text-muted-foreground" />
          }
        </td>
        <td className="px-3 py-2 font-medium text-sm">
          {doc.title}
          {doc.description && (
            <span className="ml-2 text-xs text-muted-foreground truncate max-w-[200px]">
              {doc.description}
            </span>
          )}
          {doc.folder_name && (
            <span className="ml-2 text-xs text-muted-foreground/60 italic">
              {doc.folder_name}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground">
          {doc.document_type === 'file'
            ? (
              <span className="font-mono bg-primary/10 text-primary rounded px-1">
                v{doc.current_version}
              </span>
            )
            : '—'
          }
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground">
          {doc.document_type === 'file' ? formatFileSize(doc.file_size) : '—'}
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground">
          {formatDate(doc.updated_at)}
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[96px]">
          {doc.updated_by || doc.created_by || '—'}
        </td>
        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
            {doc.document_type === 'file' && (
              <button
                className="p-1 hover:bg-muted rounded"
                title="Download"
                onClick={() => api.documents.download(doc.id, doc.file_name || doc.title)}
              >
                <Download className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
            {doc.document_type === 'link' && doc.external_url && (
              <a
                href={doc.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 hover:bg-muted rounded"
                title="Open link"
                onClick={(e) => e.stopPropagation()}
              >
                <LinkIcon className="w-3.5 h-3.5 text-muted-foreground" />
              </a>
            )}
            {doc.document_type === 'file' && (
              <button
                className="p-1 hover:bg-muted rounded"
                title="Version history"
                onClick={onToggle}
              >
                <History className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
            <button
              className="p-1 hover:bg-destructive/10 hover:text-destructive rounded"
              title="Delete"
              onClick={onDelete}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && doc.document_type === 'file' && (
        <tr key={`${doc.id}-versions`}>
          <td colSpan={8} className="p-0">
            <VersionHistory documentId={doc.id} />
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// Category Group (replaces FolderGroup — groups by semantic category)
// ============================================================================

interface CategoryGroupProps {
  category: DocumentCategory;
  documents: Document[];
  onDeleteDoc: (doc: Document) => void;
  expandedDocId: string | null;
  onToggleDoc: (docId: string) => void;
}

function CategoryGroup({
  category,
  documents,
  onDeleteDoc,
  expandedDocId,
  onToggleDoc,
}: CategoryGroupProps) {
  const [isOpen, setIsOpen] = useState(true);
  const label = CATEGORY_LABELS[category];

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Category header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-muted/40 hover:bg-muted/60 text-left"
      >
        {isOpen
          ? <FolderOpen className="w-4 h-4 text-amber-500" />
          : <Folder className="w-4 h-4 text-amber-500" />
        }
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground ml-1">
          ({documents.length})
        </span>
        <ChevronDown className={cn('w-3 h-3 text-muted-foreground ml-auto transition-transform', !isOpen && '-rotate-90')} />
      </button>

      {isOpen && (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/20 text-xs text-muted-foreground">
              <th className="px-3 py-1.5 w-8"></th>
              <th className="px-3 py-1.5 w-8"></th>
              <th className="px-3 py-1.5 text-left">Name</th>
              <th className="px-3 py-1.5 text-center w-16">Version</th>
              <th className="px-3 py-1.5 text-left w-20">Size</th>
              <th className="px-3 py-1.5 text-left w-28">Updated</th>
              <th className="px-3 py-1.5 text-left w-28">By</th>
              <th className="px-3 py-1.5 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                isExpanded={expandedDocId === doc.id}
                onToggle={() => onToggleDoc(doc.id)}
                onDelete={() => onDeleteDoc(doc)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================================
// Upload Form
// ============================================================================

interface UploadFormProps {
  folders: DocumentFolder[];
  /** When provided, offers optional deliverable linkage via V076 junction table. */
  projectId?: string;
  /**
   * When set, the uploaded document is attached to this project (project_id column).
   * Undefined = tenant-library mode (NULL project_id).
   */
  uploadProjectId?: string;
  onCancel: () => void;
}

function UploadForm({ folders, projectId, uploadProjectId, onCancel }: UploadFormProps) {
  const uploadDocument = useUploadDocument();
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: deliverables = [] } = useDeliverables(projectId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState<string>('unfiled');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  /** Optional: link uploaded document to a specific permit/milestone deliverable (V076). */
  const [linkedDeliverableId, setLinkedDeliverableId] = useState<string>('none');
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async () => {
    if (!selectedFile || !title.trim()) return;
    const result = await uploadDocument.mutateAsync({
      file: selectedFile,
      title: title.trim(),
      description: description.trim() || undefined,
      folderId: folderId !== 'unfiled' ? folderId : undefined,
      projectId: uploadProjectId,
    });
    // If a deliverable was selected, link via V076 junction table
    if (linkedDeliverableId !== 'none' && result?.document?.id) {
      try {
        await api.deliverables.documents.link(linkedDeliverableId, result.document.id);
      } catch {
        // Non-blocking — document was uploaded, link failed
        setLinkError('Document uploaded, but could not link to the selected permit/milestone. You can link it manually from the deliverable.');
        return; // Stay open so user sees the warning
      }
    }
    onCancel();
  };

  return (
    <div className="border border-dashed border-blue-300 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">File *</Label>
          <div className="flex gap-2 mt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-3.5 h-3.5 mr-1" />
              {selectedFile ? selectedFile.name : 'Choose file'}
            </Button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Title *</Label>
          <Input
            placeholder="Document title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-8 text-sm mt-1"
            autoFocus
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Description</Label>
          <Input
            placeholder="Optional description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-8 text-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Category (Folder)</Label>
          <Select value={folderId} onValueChange={setFolderId}>
            <SelectTrigger className="h-8 text-sm mt-1">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unfiled">General (unfiled)</SelectItem>
              {folders.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">
            Folders with "permit" in the name → Permit Documents. Folders with "milestone" → Internal Milestones.
          </p>
        </div>
        {/* Deliverable linkage via V076 junction table — only when projectId is available */}
        {projectId && deliverables.length > 0 ? (
          <div>
            <Label className="text-xs">Link to Permit / Milestone <span className="text-muted-foreground">(optional)</span></Label>
            <Select value={linkedDeliverableId} onValueChange={setLinkedDeliverableId}>
              <SelectTrigger className="h-8 text-sm mt-1" data-testid="deliverable-link-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {deliverables.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <Label className="text-xs text-muted-foreground">Link to Permit / Milestone</Label>
            <p className="text-[10px] text-muted-foreground mt-1" data-testid="deliverable-link-unavailable">
              {!projectId
                ? 'Open a project to link this document to a permit or milestone.'
                : 'No deliverables in this project yet — add permits or milestones first.'}
            </p>
          </div>
        )}
      </div>
      {linkError && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2 text-xs text-amber-700" role="alert">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{linkError}</span>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-8"
          onClick={handleSubmit}
          disabled={!selectedFile || !title.trim() || uploadDocument.isPending}
        >
          Upload
        </Button>
        <Button variant="ghost" size="sm" className="h-8" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

type ViewMode = 'project' | 'library';

interface DocumentsSectionProps {
  /** Project ID — enables project-scoped filtering (V148) and deliverable linkage (V076). */
  projectId?: string;
}

export function DocumentsSection({ projectId }: DocumentsSectionProps) {
  const hasDocReqs = useFeature('document_requirements_engine');

  // Default to 'project' mode when on a project page; 'library' otherwise.
  const [viewMode, setViewMode] = useState<ViewMode>(projectId ? 'project' : 'library');

  // Derive the project_id query param based on viewMode:
  //   project mode  → filter by specific project UUID
  //   library mode  → filter by 'none' (NULL project_id docs only)
  const projectFilter =
    viewMode === 'project' && projectId
      ? projectId
      : 'none';

  const { data: folderData, isLoading: foldersLoading } = useDocumentFolders();
  const { data: allDocuments = [], isLoading: docsLoading } = useDocuments({ project_id: projectFilter });
  const deleteDocument = useDeleteDocument();

  const [isUploading, setIsUploading] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);

  const folders = folderData?.folders ?? [];

  const handleToggleDoc = (docId: string) => {
    setExpandedDocId((prev) => (prev === docId ? null : docId));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteDocument.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
    if (expandedDocId === deleteTarget.id) setExpandedDocId(null);
  };

  if (foldersLoading || docsLoading) {
    return <div className="text-sm text-muted-foreground py-4">Loading documents…</div>;
  }

  // Build a lookup from folder id → folder name for classification
  const folderNameById = new Map<string, string>(
    folders.map((f) => [f.id, f.name])
  );

  // Group documents by semantic category
  const byCategory: Record<DocumentCategory, Document[]> = {
    permit: [],
    milestone: [],
    general: [],
  };

  for (const doc of allDocuments) {
    const folderName = doc.folder_id ? folderNameById.get(doc.folder_id) ?? null : null;
    const category = classifyFolder(folderName);
    byCategory[category].push(doc);
  }

  const totalCount = allDocuments.length;
  const categoryOrder: DocumentCategory[] = ['permit', 'milestone', 'general'];

  // In project mode, uploads are attached to the project.
  // In library mode, uploads have NULL project_id (tenant library).
  const uploadProjectId = viewMode === 'project' ? projectId : undefined;

  const emptyStateDescription =
    viewMode === 'project'
      ? 'Upload receipts, permits, licences, and other documents for this project'
      : 'Upload company-wide documents to the tenant library';

  return (
    <div className="space-y-3">
      {/* Requirements checklist — above docs list, shown on project pages when flag is on */}
      {projectId && hasDocReqs && (
        <DocumentRequirementsChecklist projectId={projectId} />
      )}

      {/* View mode toggle — only shown when on a project page */}
      {projectId && (
        <div className="flex items-center justify-end gap-1 shrink-0">
          <button
            onClick={() => setViewMode('project')}
            data-testid="toggle-project-docs"
            className={cn(
              'flex items-center gap-1.5 rounded-l-md border px-3 py-1.5 text-xs font-medium transition-colors',
              viewMode === 'project'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-border hover:bg-muted/60',
            )}
          >
            <Building2 className="w-3.5 h-3.5" />
            Project documents
          </button>
          <button
            onClick={() => setViewMode('library')}
            data-testid="toggle-tenant-library"
            className={cn(
              'flex items-center gap-1.5 rounded-r-md border-y border-r px-3 py-1.5 text-xs font-medium transition-colors',
              viewMode === 'library'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-border hover:bg-muted/60',
            )}
          >
            <Library className="w-3.5 h-3.5" />
            Tenant library
          </button>
        </div>
      )}

      {/* Empty state */}
      {totalCount === 0 && !isUploading && (
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description={emptyStateDescription}
          actionLabel="Upload Document"
          onAction={() => setIsUploading(true)}
        />
      )}

      {/* Summary bar */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {totalCount} document{totalCount !== 1 ? 's' : ''}
          </span>
          <Button variant="outline" size="sm" onClick={() => setIsUploading(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Upload
          </Button>
        </div>
      )}

      {/* Upload form */}
      {isUploading && (
        <UploadForm
          folders={folders}
          projectId={projectId}
          uploadProjectId={uploadProjectId}
          onCancel={() => setIsUploading(false)}
        />
      )}

      {/* Category groups — Permit Documents / Internal Milestones / General */}
      {totalCount > 0 && (
        <div className="space-y-3">
          {categoryOrder.map((category) => {
            const docs = byCategory[category];
            if (docs.length === 0) return null;
            return (
              <CategoryGroup
                key={category}
                category={category}
                documents={docs}
                onDeleteDoc={setDeleteTarget}
                expandedDocId={expandedDocId}
                onToggleDoc={handleToggleDoc}
              />
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.title}" and all its version history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
